import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ruler, HelpCircle, Instagram, MessageCircle, CheckCircle, Shirt, Info, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import Layout from '../components/layout/Layout';
import { SIZE_CHART, BRAND } from '../data/mock';

const WHATSAPP = '918889616972';
const INSTAGRAM_USER = 'aura_printiqo';

const SizeGuidePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    size: '',
    fit: '',
    additionalInfo: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const popularBrands = [
    'H&M', 'Zara', 'Uniqlo', 'Nike', 'Adidas', 'Puma',
    "Levi's", 'US Polo', 'Allen Solly', 'Van Heusen',
    'Peter England', 'Bewakoof', 'The Souled Store',
    'Roadster', 'HRX', 'Other'
  ];

  const fitTypes = ['Slim Fit', 'Regular Fit', 'Relaxed/Loose Fit', 'Oversized'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buildMessage = () =>
    `Hi AuraPrintiqo! 👋\n\n*Size Finder Request*\n━━━━━━━━━━━━━━━━\n*Name:* ${formData.name}\n\n*My Current T-shirt Reference:*\n• Brand: ${formData.brand || 'Not specified'}\n• Size I wear: ${formData.size || 'Not specified'}\n• Fit preference: ${formData.fit || 'Not specified'}\n\n*Additional Info:*\n${formData.additionalInfo || 'None'}\n\nPlease help me find the right size for my order!`;

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(buildMessage())}`,
      '_blank',
      'noopener,noreferrer'
    );
    setSubmitted(true);
  };

  const handleInstagramSubmit = (e) => {
    e.preventDefault();
    const msg = buildMessage()
      .replace(/\n/g, '%0A')
      .replace(/ /g, '%20')
      .replace(/\*/g, '')
      .replace(/━/g, '-');
    window.open(`https://ig.me/m/${INSTAGRAM_USER}?text=${msg}`, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
  };

  const measurementTips = [
    { title: 'Chest', description: 'Measure around the fullest part of your chest, keeping the tape horizontal.' },
    { title: 'Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
    { title: 'Shoulders', description: 'Measure from one shoulder edge to the other across the back.' }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--mint))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
              <Ruler className="w-4 h-4 inline mr-1" />
              Size Guide
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Find Your
              <span className="gradient-text"> Perfect Fit</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Don't let sizing confusion stop you! Use our detailed size chart or let our team
              help you find the perfect size based on your favorite t-shirt.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Banner */}
      <section className="py-8 bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Sparkles className="w-6 h-6" />
              <span className="font-semibold">Not sure about your size?</span>
              <span className="hidden sm:inline">Our AI + team will help you find the perfect fit!</span>
            </div>
            <a href="#size-finder">
              <Button className="bg-white text-gray-900 hover:bg-gray-100">
                Get Personalized Size
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Size Charts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Size Charts</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              All measurements are in inches. For the best fit, compare these measurements
              with a t-shirt that fits you well.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-0 shadow-soft overflow-hidden">
              <div className="bg-[hsl(var(--mint))] p-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shirt className="w-5 h-5" />
                  T-Shirt Size Chart
                </h3>
                <p className="text-sm text-gray-700 mt-1">Premium 180 GSM Combed Cotton</p>
              </div>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Size</TableHead>
                      <TableHead className="font-semibold">Chest (inches)</TableHead>
                      <TableHead className="font-semibold">Length (inches)</TableHead>
                      <TableHead className="font-semibold">Best For</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {SIZE_CHART.tshirt.map((row, index) => (
                      <TableRow key={row.size} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <TableCell className="font-bold text-[hsl(350,70%,55%)]">{row.size}</TableCell>
                        <TableCell>{row.chest}"</TableCell>
                        <TableCell>{row.length}"</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {row.size === 'S' && 'Chest 34-36"'}
                          {row.size === 'M' && 'Chest 36-38"'}
                          {row.size === 'L' && 'Chest 38-40"'}
                          {row.size === 'XL' && 'Chest 40-42"'}
                          {row.size === 'XXL' && 'Chest 42-44"'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft overflow-hidden">
              <div className="bg-[hsl(var(--lavender))] p-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shirt className="w-5 h-5" />
                  Hoodie Size Chart
                </h3>
                <p className="text-sm text-gray-700 mt-1">Premium 320 GSM Cotton Fleece</p>
              </div>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Size</TableHead>
                      <TableHead className="font-semibold">Chest (inches)</TableHead>
                      <TableHead className="font-semibold">Length (inches)</TableHead>
                      <TableHead className="font-semibold">Best For</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {SIZE_CHART.hoodie.map((row, index) => (
                      <TableRow key={row.size} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <TableCell className="font-bold text-[hsl(var(--lavender-dark))]">{row.size}</TableCell>
                        <TableCell>{row.chest}"</TableCell>
                        <TableCell>{row.length}"</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {row.size === 'S' && 'Chest 34-36"'}
                          {row.size === 'M' && 'Chest 36-38"'}
                          {row.size === 'L' && 'Chest 38-40"'}
                          {row.size === 'XL' && 'Chest 40-42"'}
                          {row.size === 'XXL' && 'Chest 42-44"'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-6 bg-[hsl(var(--cream))] rounded-2xl">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Fit Note</h4>
                <p className="text-gray-600 text-sm">
                  Our t-shirts and hoodies have a <strong>regular fit</strong> — not too tight, not too loose.
                  If you prefer a looser/oversized fit, we recommend going one size up.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Measure */}
      <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Measure</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              For the most accurate fit, measure a t-shirt that fits you well and compare with our size chart.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {measurementTips.map((tip, index) => (
              <Card key={index} className="border-0 shadow-soft">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[hsl(350,70%,65%)]/10 to-[hsl(20,90%,70%)]/10 flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-[hsl(350,70%,60%)]">{index + 1}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="text-sm text-gray-600">{tip.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 p-8 bg-white rounded-2xl shadow-soft">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Quick Measurement Guide</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                {[
                  'Lay your best-fitting t-shirt flat on a surface',
                  'Measure chest from armpit to armpit and multiply by 2',
                  'Measure length from shoulder seam to bottom hem',
                  'Compare with our size chart above'
                ].map((tip, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-600">{tip}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center">
                <div className="relative w-48 h-56 bg-[hsl(var(--mint))]/30 rounded-lg flex items-center justify-center">
                  <Shirt className="w-32 h-32 text-[hsl(var(--mint-dark))]/50" />
                  <div className="absolute top-1/2 left-0 right-0 border-t-2 border-dashed border-[hsl(350,70%,60%)]">
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-[hsl(350,70%,55%)] bg-white px-2">CHEST</span>
                  </div>
                  <div className="absolute top-4 bottom-4 left-1/2 border-l-2 border-dashed border-[hsl(20,90%,60%)]">
                    <span className="absolute top-1/2 -translate-y-1/2 left-2 text-xs font-semibold text-[hsl(20,90%,55%)] bg-white px-1 whitespace-nowrap">LENGTH</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Size Finder Form */}
      <section id="size-finder" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--coral-light))]/50 rounded-full text-sm font-medium text-[hsl(var(--coral))] mb-4">
              <Sparkles className="w-4 h-4 inline mr-1" />
              AI-Powered Size Finder
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Confused? Let Us Help!</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Tell us about a t-shirt that fits you perfectly and we'll recommend your ideal size.
            </p>
          </div>

          <Card className="border-0 shadow-soft-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] p-6 text-white">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                Personalized Size Recommendation
              </h3>
              <p className="text-white/90 text-sm mt-1">
                Share details of your best-fitting t-shirt and we'll find your perfect size
              </p>
            </div>

            <CardContent className="p-8">
              {!submitted ? (
                <form className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">Your Name *</Label>
                    <Input
                      id="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 font-medium">Which brand's t-shirt fits you best?</Label>
                    <Select onValueChange={(value) => handleInputChange('brand', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {popularBrands.map((brand) => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-700 font-medium">What size do you wear in that brand? *</Label>
                    <Select onValueChange={(value) => handleInputChange('size', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select your size" />
                      </SelectTrigger>
                      <SelectContent>
                        {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-700 font-medium">How does that t-shirt fit you?</Label>
                    <Select onValueChange={(value) => handleInputChange('fit', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select fit type" />
                      </SelectTrigger>
                      <SelectContent>
                        {fitTypes.map((fit) => (
                          <SelectItem key={fit} value={fit}>{fit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="additionalInfo" className="text-gray-700 font-medium">
                      Any additional details? (Optional)
                    </Label>
                    <Textarea
                      id="additionalInfo"
                      placeholder="E.g., 'I have broad shoulders', 'Height: 5'10, Weight: 75kg'"
                      value={formData.additionalInfo}
                      onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                      className="mt-2"
                      rows={3}
                    />
                  </div>

                  <div className="pt-4 space-y-4">
                    <p className="text-sm text-gray-500 text-center font-medium">
                      Choose how to send your size request:
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        type="button"
                        onClick={handleWhatsAppSubmit}
                        disabled={!formData.name || !formData.size}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white py-6 text-base"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Send via WhatsApp
                      </Button>
                      <Button
                        type="button"
                        onClick={handleInstagramSubmit}
                        disabled={!formData.name || !formData.size}
                        className="flex-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white py-6 text-base"
                      >
                        <Instagram className="w-5 h-5 mr-2" />
                        Send via Instagram DM
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 text-center">
                      * Name and Size are required. Clicking sends your details directly to us.
                    </p>
                  </div>
                </form>
              ) : (
                <div className="text-center py-8">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Sent!</h3>
                  <p className="text-gray-600 mb-2">
                    Your size request has been sent via WhatsApp / Instagram DM.
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    We'll get back to you with your perfect size recommendation within 2–4 hours.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline">
                    Submit Another Request
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Share Details', desc: "Tell us your best-fitting brand & size" },
              { step: '2', title: 'AI Analysis', desc: 'Our AI compares brand sizing data' },
              { step: '3', title: 'Get Recommendation', desc: 'Receive your perfect AuraPrintiqo size' }
            ].map((item) => (
              <div key={item.step} className="flex items-center gap-3 p-4 bg-[hsl(var(--cream))] rounded-xl">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] text-white font-bold flex items-center justify-center flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Size-Related FAQs</h2>
          <div className="space-y-4">
            {[
              { q: "What if I'm between two sizes?", a: "If you're between sizes, we recommend going for the larger size for a comfortable fit. If you prefer a fitted look, go with the smaller size." },
              { q: "Do your t-shirts shrink after washing?", a: "Our premium combed cotton is pre-shrunk, so shrinkage is minimal (less than 3%). We recommend washing in cold water and air drying for best results." },
              { q: "Can I exchange if the size doesn't fit?", a: "Yes! We offer hassle-free size exchanges within 7 days of delivery. Just reach out to us on Instagram or WhatsApp." },
              { q: "Are your sizes true to Indian sizing?", a: "Yes, our sizes follow standard Indian measurements. They're comparable to brands like Bewakoof, The Souled Store, and similar Indian D2C brands." }
            ].map((faq, index) => (
              <Card key={index} className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">{faq.q}</h4>
                  <p className="text-gray-600 text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Order?</h2>
          <p className="text-gray-300 mb-8">Now that you know your size, create your custom anime print!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/collections">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] hover:from-[hsl(350,70%,60%)] hover:to-[hsl(20,90%,65%)] text-white px-8">
                Browse Collections
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SizeGuidePage;
